import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { AnalysisJobItem } from "@/types/analysis";

export interface SpatialState {
  analysisJobs: AnalysisJobItem[];
  unOpenedJobIds: string[];
  queuedJobIds: string[];
}

export const analysisJobInitialState: SpatialState = {
  analysisJobs: [], // List of all analysis jobs
  unOpenedJobIds: [], // IDs of completed analysis jobs (owned by user) that have not been opened, used for notification indicators
  queuedJobIds: [] // IDs of analysis jobs (owned by user) that are currently queued or have ever been queued, used for toast notifications and controlling open flow
};

export const analysisJob = createSlice({
  name: "analysisJob",
  initialState: analysisJobInitialState,
  reducers: {
    upsertAnalysisJob: (state, action: PayloadAction<AnalysisJobItem[]>) => {
      const items: AnalysisJobItem[] = Array.isArray(action.payload)
        ? action.payload
        : [action.payload];

      const analysisJobsMap = new Map(
        state.analysisJobs.map((job) => [job.id, job])
      );

      for (const item of items) {
        analysisJobsMap.set(item.id, {
          ...(analysisJobsMap.get(item.id) || {}),
          ...item
        });
      }

      let result = Array.from(analysisJobsMap.values());

      if (items.length > 1) {
        const itemIds = new Set(items.map((item) => item.id));
        result = result.filter((job) => itemIds.has(job.id));
      }

      state.analysisJobs = Array.from(result).sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    },
    addUnOpenedJobIds: (state, action: PayloadAction<string | undefined>) => {
      if (action.payload && !state.unOpenedJobIds.includes(action.payload)) {
        state.unOpenedJobIds = [...state.unOpenedJobIds, action.payload];
      }
    },
    removeUnOpenedJobId: (state, action: PayloadAction<string | undefined>) => {
      state.unOpenedJobIds = state.unOpenedJobIds.filter(
        (id) => id !== action.payload
      );
    },
    addQueuedJobId: (state, action: PayloadAction<string | undefined>) => {
      if (action.payload && !state.queuedJobIds.includes(action.payload)) {
        state.queuedJobIds = [...state.queuedJobIds, action.payload];
      }
    },
    removeQueuedJobId: (state, action: PayloadAction<string | undefined>) => {
      state.queuedJobIds = state.queuedJobIds.filter(
        (id) => id !== action.payload
      );
    },
    resetAnalysisJob: () => analysisJobInitialState
  }
});

export const {
  upsertAnalysisJob,
  addUnOpenedJobIds,
  removeUnOpenedJobId,
  addQueuedJobId,
  removeQueuedJobId,
  resetAnalysisJob
} = analysisJob.actions;

export const analysisJobReducer = analysisJob.reducer;
